import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import { i18n } from 'config/translations'

export default class extends Controller {
  static targets = [
    'imageUploadSection',
    'imageStatus',
    'imageListStatus',
    'inputFieldStatus',
    'errorStatus',
    'nicknameSection',
    'nicknameInputWrapper',
    'signedIdsContainer',
    'dropZone',
    'submitButton',
    'uploadingNotice',
  ]

  initialize() {
    this.selectedFiles = []
    this.uploadedBlobs = [] // アップロード済みのblob情報を保持
    this.pendingUploads = 0
    this.toggleImageUploadOnLoad()
  }

  connect() {
    this.updateUploadState()
  }

  toggleImageUpload(event) {
    const value = event?.target?.value === 'true'
    if (value) {
      this.imageUploadSectionTarget.classList.remove('hidden')
      this.nicknameSectionTarget.classList.remove('hidden')

      if (!this.imageListStatusTarget.querySelector('[data-mypage--form--review-target="imageStatus"]')) {
        const newImageStatus = document.createElement('div')
        newImageStatus.className = 'image-container mt-4 mr-4 hidden'
        newImageStatus.setAttribute('data-mypage--form--review-target', 'imageStatus')

        const innerHtml = `
          <div class="relative border border-gray-300 rounded-md overflow-hidden">
            <img class="object-cover w-full h-full rounded-md" data-mypage--form--review-target="imagePreview">
            <div class="delete-btn absolute top-0 right-0 bg-gray-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs cursor-pointer" data-action="click->mypage--form--review#deleteFile" data-mypage--form--review-target="deleteButton">×</div>
          </div>
        `
        newImageStatus.innerHTML = innerHtml
        this.imageListStatusTarget.appendChild(newImageStatus)
      }
    } else {
      this.imageUploadSectionTarget.classList.add('hidden')
      this.nicknameSectionTarget.classList.add('hidden')
      this.clearSelectedFiles()
    }
  }

  toggleImageUploadOnLoad() {
    const checkedOption = document.querySelector('input[name="review[published]"]:checked')
    if (checkedOption && checkedOption.value === 'true') {
      this.imageUploadSectionTarget.classList.remove('hidden')
      this.nicknameSectionTarget.classList.remove('hidden')
    } else {
      this.imageUploadSectionTarget.classList.add('hidden')
      this.nicknameSectionTarget.classList.add('hidden')
    }
  }

  displayImagePreview(e) {
    const files = Array.from(e.target.files)
    this.processFiles(files)
  }

  uploadFile(file, uniqueId) {
    const input = this.inputFieldStatusTarget.querySelector('input[type="file"]')
    const url = input.dataset.directUploadUrl

    const upload = new DirectUpload(file, url, this)
    this.onUploadStart()

    upload.create((error, blob) => {
      const imageContainer = this.imageListStatusTarget.querySelector(`[data-id="${uniqueId}"]`)

      if (error) {
        console.error('Direct upload error:', error)
        this.showErrorMessage('画像のアップロードに失敗しました')
        // エラー時はプレビューを削除
        if (imageContainer) {
          imageContainer.remove()
        }
        this.selectedFiles = this.selectedFiles.filter((item) => item.id !== uniqueId)
      } else {
        // アップロード成功: signed_idを保持
        this.uploadedBlobs.push({ id: uniqueId, signedId: blob.signed_id })
        // hidden inputを追加
        this.addSignedIdInput(blob.signed_id, uniqueId)
        // アップロード完了のインジケーターを更新
        if (imageContainer) {
          this.removeUploadingIndicator(imageContainer)
        }
      }
      this.onUploadComplete()
    })
  }

  addUploadingIndicator(container) {
    const indicator = document.createElement('div')
    indicator.className = 'uploading-indicator absolute inset-0 bg-black/50 flex items-center justify-center'
    indicator.innerHTML = `
      <svg class="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `
    const relativeContainer = container.querySelector('.relative')
    if (relativeContainer) {
      relativeContainer.appendChild(indicator)
    }
  }

  removeUploadingIndicator(container) {
    const indicator = container.querySelector('.uploading-indicator')
    if (indicator) {
      indicator.remove()
    }
  }

  addSignedIdInput(signedId, uniqueId) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'review[image_signed_ids][]'
    input.value = signedId
    input.dataset.uniqueId = uniqueId
    this.signedIdsContainerTarget.appendChild(input)
  }

  removeSignedIdInput(uniqueId) {
    const input = this.signedIdsContainerTarget.querySelector(`input[data-unique-id="${uniqueId}"]`)
    if (input) {
      input.remove()
    }
  }

  deleteFile(e) {
    const fileNode = e.currentTarget.closest('.image-container')
    if (!fileNode) return

    const fileId = fileNode.getAttribute('data-id')
    if (fileId) {
      this.selectedFiles = this.selectedFiles.filter((item) => item.id !== fileId)
      this.uploadedBlobs = this.uploadedBlobs.filter((item) => item.id !== fileId)
      this.removeSignedIdInput(fileId)
      fileNode.remove()
    }
  }

  showErrorMessage(message) {
    let errorMessageEl = this.errorStatusTarget.querySelector('.error-message')
    if (!errorMessageEl) {
      errorMessageEl = document.createElement('p')
      errorMessageEl.className = 'error-message text-red-700'
      errorMessageEl.style.fontSize = '12px'
      this.errorStatusTarget.appendChild(errorMessageEl)
    }
    errorMessageEl.textContent = message
  }

  clearErrorMessage() {
    const errorMessageEl = this.errorStatusTarget.querySelector('.error-message')
    if (errorMessageEl) {
      errorMessageEl.remove()
    }
  }

  clearSelectedFiles() {
    this.selectedFiles = []
    this.uploadedBlobs = []
    this.imageListStatusTarget.innerHTML = ''
    this.signedIdsContainerTarget.innerHTML = ''
    this.pendingUploads = 0
    this.updateUploadState()
  }

  // DirectUploadのコールバック（プログレス表示用）
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener('progress', (event) => {
      const progress = (event.loaded / event.total) * 100
      console.log(`Upload progress: ${progress.toFixed(1)}%`)
    })
  }

  // ドラッグ＆ドロップ: dragover イベント
  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  // ドラッグ＆ドロップ: dragenter イベント（ドロップゾーンにドラッグが入った時）
  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add('border-blue-500', 'bg-blue-50')
      this.dropZoneTarget.classList.remove('border-gray-300', 'bg-gray-50')
    }
  }

  // ドラッグ＆ドロップ: dragleave イベント（ドロップゾーンからドラッグが出た時）
  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
      this.dropZoneTarget.classList.add('border-gray-300', 'bg-gray-50')
    }
  }

  // ドラッグ＆ドロップ: drop イベント（ファイルがドロップされた時）
  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()

    // ドロップゾーンのスタイルをリセット
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
      this.dropZoneTarget.classList.add('border-gray-300', 'bg-gray-50')
    }

    const files = Array.from(e.dataTransfer.files)
    // 画像ファイルのみをフィルタリング
    const imageFiles = files.filter((file) => file.type.match(/^image\/(jpeg|gif|png)$/))

    if (imageFiles.length === 0) {
      this.showErrorMessage('JPEG、GIF、PNG形式の画像のみアップロードできます')
      return
    }

    if (imageFiles.length !== files.length) {
      this.showErrorMessage('一部の非対応ファイルは除外されました')
    }

    // ファイルを処理（displayImagePreviewと同じロジック）
    this.processFiles(imageFiles)
  }

  // ファイルを処理する共通メソッド（ファイル選択とドラッグ＆ドロップで共通）
  processFiles(files) {
    const maxFileSize = 5 * 1024 * 1024
    const maxFileCount = 10
    const fileSizeErrorMessage = i18n.t('views.mypage.form.review_form.image_size_error')
    const fileCountErrorMessage = i18n.t('views.mypage.form.review_form.image_count_error')

    this.clearErrorMessage()

    if (this.selectedFiles.length + files.length > maxFileCount) {
      this.showErrorMessage(fileCountErrorMessage)
      return
    }

    files.forEach((file, index) => {
      if (file.size > maxFileSize) {
        this.showErrorMessage(fileSizeErrorMessage)
        return
      }

      const uniqueId = `${Date.now()}-${index}`
      this.selectedFiles.push({ id: uniqueId, file })

      // プレビュー表示
      const reader = new FileReader()
      reader.onload = (event) => {
        let imageStatusTarget = this.imageListStatusTarget.querySelector('[data-mypage--form--review-target="imageStatus"]')

        if (!imageStatusTarget) {
          const newImageStatus = document.createElement('div')
          newImageStatus.className = 'image-container mt-4 mr-4'
          newImageStatus.setAttribute('data-mypage--form--review-target', 'imageStatus')

          const innerHtml = `
            <div class="relative border border-gray-300 rounded-md overflow-hidden">
              <img class="object-cover w-full h-full rounded-md" data-mypage--form--review-target="imagePreview">
              <div class="delete-btn absolute top-0 right-0 bg-gray-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs cursor-pointer" data-action="click->mypage--form--review#deleteFile" data-mypage--form--review-target="deleteButton">×</div>
            </div>
          `
          newImageStatus.innerHTML = innerHtml
          this.imageListStatusTarget.appendChild(newImageStatus)
          imageStatusTarget = newImageStatus
        }
        const clonedNode = imageStatusTarget.cloneNode(true)
        clonedNode.classList.remove('hidden')
        clonedNode.querySelector('[data-mypage--form--review-target="imagePreview"]').src = event.target.result
        clonedNode.setAttribute('data-id', uniqueId)
        // アップロード中のインジケーターを追加
        this.addUploadingIndicator(clonedNode)
        this.imageListStatusTarget.appendChild(clonedNode)
      }
      reader.readAsDataURL(file)

      // ダイレクトアップロードを実行
      this.uploadFile(file, uniqueId)
    })
  }

  handleSubmitClick(event) {
    if (this.pendingUploads > 0) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
  }

  handleFormSubmit(event) {
    if (this.pendingUploads > 0) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.showErrorMessage(i18n.t('views.mypage.form.review_form.uploading_notice'))
    }
  }

  onUploadStart() {
    this.pendingUploads += 1
    this.updateUploadState()
  }

  onUploadComplete() {
    this.pendingUploads = Math.max(0, this.pendingUploads - 1)
    this.updateUploadState()
  }

  updateUploadState() {
    const isUploading = this.pendingUploads > 0
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isUploading
      this.submitButtonTarget.classList.toggle('opacity-50', isUploading)
      this.submitButtonTarget.classList.toggle('cursor-not-allowed', isUploading)
    }
    if (this.hasUploadingNoticeTarget) {
      this.uploadingNoticeTarget.classList.toggle('hidden', !isUploading)
    }
  }
}
